import type {
  ActivityType,
  ActivityLookupByType,
  FoodActivity,
  FoodSubEntry,
  LiquidActivity,
  LiquidSubEntry,
  SubEntryLookupByType,
  SubEntryType,
} from "@tandemdiabetes/sugarmate-api-react-client"

export interface Aggregate {
  is_aggregate: boolean;
}

// TODO typescript doesn't know that ActivityLookupByType and SubEntryLookupByType overlap
function aggregateSubEntries<T extends SubEntryType>(
  aggregate: SubEntryLookupByType<T>,
  subEntry: SubEntryLookupByType<T>,
  aggregateActivities: (
    aggregate: ActivityLookupByType<T>,
    activity: ActivityLookupByType<T>,
  ) => ActivityLookupByType<T>,
): SubEntryLookupByType<T> & Aggregate {
  const activityType = aggregate.subentry_type as keyof SubEntryLookupByType<T>

  return {
    is_aggregate: true,
    _id: aggregate._id,
    image_id: null,
    image_url: null,
    thumbnail: null,
    note: null,
    duration_in_seconds: null,
    subentry_type: activityType,
    [activityType]: aggregateActivities(
      aggregate[activityType] as ActivityLookupByType<T>,
      subEntry[activityType] as ActivityLookupByType<T>,
    ),
  } as unknown as SubEntryLookupByType<T> & Aggregate
}

function sumOrNullish(
  a: number | null | undefined,
  b: number | null | undefined,
): number | null | undefined {
  const aNullish = a === null || a === undefined
  const bNullish = b === null || b === undefined

  if (aNullish) {
    return b
  }

  if (bNullish) {
    return a
  }

  return a + b
}

function aggregateFoodActivites(
  aggregate: FoodActivity,
  activity: FoodActivity,
): FoodActivity {
  return {
    // clear the title and nutritional reference
    title: "",
    nutritional_reference: null,
    // sum the nutrition info
    carbs: sumOrNullish(
      aggregate.carbs,
      activity.carbs,
    ),
    fiber: sumOrNullish(
      aggregate.fiber,
      activity.fiber,
    ),
    fat: sumOrNullish(
      aggregate.fat,
      activity.fat,
    ),
    sugar: sumOrNullish(
      aggregate.sugar,
      activity.sugar,
    ),
    protein: sumOrNullish(
      aggregate.protein,
      activity.protein,
    ),
    calories: sumOrNullish(
      aggregate.calories,
      activity.calories,
    ),
  }
}

export function aggregateFoodSubEntries(
  aggregate: FoodSubEntry,
  subEntry: FoodSubEntry,
): FoodSubEntry & Aggregate {
  return aggregateSubEntries<ActivityType.Food>(
    aggregate,
    subEntry,
    aggregateFoodActivites,
  )
}

function aggregateLiquidActivites(
  aggregate: LiquidActivity,
  activity: LiquidActivity,
): LiquidActivity {
  return {
    // set liquid_type to undefined unless both liquid types match
    liquid_type:
      aggregate.liquid_type === activity.liquid_type
        ? aggregate.liquid_type
        : undefined,
    // set the units to whatever is used in the aggregate
    units: aggregate.units,
    // sum the microliters
    microliters: aggregate.microliters + activity.microliters,
  }
}

export function aggregateLiquidSubEntries(
  aggregate: LiquidSubEntry,
  subEntry: LiquidSubEntry,
): LiquidSubEntry & Aggregate {
  return aggregateSubEntries<ActivityType.Liquid>(
    aggregate,
    subEntry,
    aggregateLiquidActivites,
  )
}
